import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Coffee, Package, Send, Copy, Share2, Check, Coins, Tag, Gift, AlertCircle } from 'lucide-react';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { Header } from '@core/components/Header';
import { useProfile } from '@core/contexts/ProfileContext';
import { useIdentity } from '@core/contexts/IdentityContext';
import { useVenue } from '@core/contexts/VenueContext';
import { coreRuntime } from '@core/runtime';
import { getGiftTemplates, createGiftLink, type GiftLinkData, type GiftTemplatePublic } from '@core/api/gifts';

// Icon by gift type
function GiftTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'BONUS_POINTS':
      return <Coins className="w-5 h-5" />;
    case 'PRODUCT':
      return <Coffee className="w-5 h-5" />;
    case 'SUBSCRIPTION':
      return <Package className="w-5 h-5" />;
    case 'PROMO_CODE':
      return <Tag className="w-5 h-5" />;
    default:
      return <Gift className="w-5 h-5" />;
  }
}

// Template value label
function getTemplateValueLabel(tpl: GiftTemplatePublic): string {
  const v = tpl.value;
  switch (tpl.type) {
    case 'BONUS_POINTS':
      return `${v.points ?? 0} баллов`;
    case 'PRODUCT':
      return String(v.rewardName ?? 'Продукт');
    case 'SUBSCRIPTION':
      return String(v.planName ?? 'Абонемент');
    case 'PROMO_CODE':
      return `Скидка ${v.discountPercent ?? 0}%`;
    default:
      return '';
  }
}

export function SendGiftPage() {
  const navigate = useNavigate();
  // Отправитель — клиент сессии входа (K-002): дарить можно и из мини-аппа, и из PWA. Telegram ID — только
  // необязательная сверка (в PWA его нет и в запрос он не уходит).
  const { user: profileUser } = useProfile();
  const { telegramId } = useIdentity();
  const { venue } = useVenue();
  const userId = profileUser?.id ?? null;

  const [templates, setTemplates] = useState<GiftTemplatePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [giftLink, setGiftLink] = useState<GiftLinkData | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getGiftTemplates();
        setTemplates(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load gift templates:', error);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleSend = async () => {
    if (!userId || !selectedId) return;

    const tpl = templates.find(t => t.id === selectedId);
    if (!tpl) return;

    setError(null);
    setSending(true);
    try {
      const link = await createGiftLink({
        telegramId,
        templateId: tpl.id,
        message: message || undefined,
      });
      setGiftLink(link);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Не удалось отправить подарок';
      console.error('Failed to create gift link:', err);
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  // Ссылка ведёт в тот же вход, из которого дарят: мини-апп — t.me (startapp=<slug>--gift<код>),
  // PWA — сайт PWA заведения (?gift=<код>, подарок забирается после входа по телефону).
  const shareUrl = giftLink && venue ? coreRuntime().links.gift(venue, giftLink) ?? '' : '';

  // Красивый текст-приглашение, который уходит другу (без ссылки — её окно «поделиться» добавит само).
  const shareCaption = giftLink
    ? [
        `🎁 Тебе подарок — ${giftLink.title}!`,
        message.trim() ? `\n💬 «${message.trim()}»` : '',
        '\nЗабери его в приложении 👇',
      ]
        .filter(Boolean)
        .join('\n')
    : '';

  const handleCopy = async () => {
    // В буфер — текст с пожеланием + ссылка (у копии нет отдельного url-поля, как у Telegram-share).
    const clip = `${shareCaption}\n${shareUrl}`;
    try {
      await navigator.clipboard.writeText(clip);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = clip;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // «Поделиться» — средствами входа: мини-апп открывает выбор чата Telegram; без Telegram — системное окно
  // «поделиться» (Web Share API), а где его нет — копирование.
  const handleShare = async () => {
    if (!shareUrl) return;
    const rt = coreRuntime();
    if (rt.openShareDialog) {
      rt.openShareDialog(shareUrl, shareCaption);
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: giftLink?.title, text: shareCaption, url: shareUrl });
      } catch {
        // Отмена окна — не ошибка.
      }
      return;
    }
    await handleCopy();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-main">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  // Show share screen after link is created
  if (giftLink) {
    return (
      <div className="p-4 space-y-4">
        <Header title="Подарок создан!" showBack />

        <Card className="text-center py-6 border-2 border-primary/30">
          <div className="text-4xl mb-3">🎁</div>
          <div className="font-bold text-lg text-text-main mb-1">{giftLink.title}</div>
          <div className="text-sm text-text-muted mb-4">
            Отправьте ссылку другу, чтобы он получил подарок
          </div>

          <div className="bg-secondary/20 rounded-lg p-3 text-xs text-text-muted break-all mb-4 mx-4">
            {shareUrl}
          </div>

          <div className="flex gap-3 px-4">
            <Button
              variant="outline"
              onClick={handleCopy}
              className="flex-1 min-w-0 flex items-center justify-center gap-2"
            >
              {copied ? <Check className="w-4 h-4 flex-shrink-0" /> : <Copy className="w-4 h-4 flex-shrink-0" />}
              <span className="truncate">{copied ? 'Скопировано!' : 'Копировать'}</span>
            </Button>
            <Button
              onClick={() => void handleShare()}
              className="flex-1 min-w-0 flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">Поделиться</span>
            </Button>
          </div>
        </Card>

        <Button
          fullWidth
          variant="outline"
          onClick={() => navigate('/gifts')}
        >
          К подаркам
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <Header title="Подарить другу" showBack />

      {/* Gift templates list */}
      <div className="space-y-3">
        {templates.length === 0 ? (
          <Card className="text-center py-6 text-text-muted">
            <Gift className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <div>Нет доступных подарков</div>
          </Card>
        ) : (
          templates.map(tpl => (
            <Card
              key={tpl.id}
              className={`cursor-pointer transition-all ${
                selectedId === tpl.id
                  ? 'border-2 border-primary bg-primary/5'
                  : 'border border-secondary/30'
              }`}
              onClick={() => setSelectedId(prev => prev === tpl.id ? '' : tpl.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <GiftTypeIcon type={tpl.type} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-text-main">{tpl.title}</div>
                  <div className="text-sm text-text-muted">{getTemplateValueLabel(tpl)}</div>
                </div>
                {tpl.costInPoints != null && tpl.costInPoints > 0 && (
                  <div className="flex items-center gap-1 font-semibold text-primary flex-shrink-0">
                    <Coins className="w-4 h-4" />
                    {tpl.costInPoints}
                  </div>
                )}
                {selectedId === tpl.id && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Message (optional) */}
      {selectedId && (
        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">
            Сообщение (необязательно)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Напиши пожелание..."
            rows={2}
            className="w-full px-4 py-3 bg-secondary/10 border border-secondary/30 rounded-xl text-text-on-secondary placeholder-text-on-secondary-muted/50 resize-none focus:outline-none focus:border-primary"
          />
        </div>
      )}

      {/* Error (e.g. недостаточно баллов) */}
      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-500/25 bg-red-500/10 px-4 py-3">
          <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-500/20">
            <AlertCircle className="h-4 w-4 text-red-400" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-red-400">Не получилось отправить</div>
            <div className="text-sm text-text-muted break-words">{error}</div>
          </div>
        </div>
      )}

      {/* Send button */}
      <Button
        fullWidth
        onClick={handleSend}
        disabled={!selectedId || sending}
        className="flex items-center justify-center gap-2"
      >
        {sending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        {sending ? 'Отправляем...' : 'Подарить'}
      </Button>
    </div>
  );
}
